<template>
  <div class="crud_form_wrapper">
    <div class="form_title_wrapper">
      <h4>{{ $t("MESSAGES.Subscription management") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #405950">
        <i
          :class="$i18n.locale === 'en' ? 'fas fa-forward' : 'fas fa-backward'"
        ></i>
      </v-btn>
    </div>
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <div class="row">
          <base-input
            col="6"
            type="text"
            :placeholder="$t('MESSAGES.Package name ar')"
            v-model.trim="data.name_ar"
          />
          <base-input
            col="6"
            type="text"
            :placeholder="$t('MESSAGES.Package name en')"
            v-model.trim="data.name_en"
          />
          <base-input
            col="6"
            type="textarea"
            :placeholder="$t('MESSAGES.Package features ar')"
            v-model.trim="data.features_ar"
          />
          <base-input
            col="6"
            type="textarea"
            :placeholder="$t('MESSAGES.Package features en')"
            v-model.trim="data.features_en"
          />

          <base-input
            col="6"
            type="number"
            :placeholder="$t('MESSAGES.Package duration by month')"
            v-model.trim="data.duration"
          />

          <base-input
            col="6"
            type="number"
            :placeholder="$t('MESSAGES.Package price')"
            v-model.trim="data.price"
          />

          <base-select-input
            col="12"
            :optionsList="allUsers"
            :placeholder="$t('MESSAGES.Select clients')"
            v-model="data.users"
            :loading="clientsPagination.isLoading"
            :enableInfiniteScroll="true"
            @reachEnd="handleClientsReachEnd"
            @search-change="handleClientsSearch"
            multiple
          />

          <div class="btn_wrapper">
            <base-button
              class="mt-2"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
        </div>
      </form>
      <button class="btn primary_btn" style="background: var(--main_theme_clr)">
        <router-link
          to="/store-settings/subscribers"
          style="
            background: var(--main_theme_clr);
            color: var(--white_clr) !important;
          "
        >
          {{ $t("MESSAGES.Package subscribers") }}
        </router-link>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "GeneralSettings",

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data

      allUsers: [],
      clientsPagination: {
        page: 0,
        limit: 20,
        search: "",
        hasMore: true,
        isLoading: false,
      },
      clientsSearchTimeout: null,

      // Start:: Data Collection To Send
      data: {
        id: null,
        duration: null,
        features: null,
        features_ar: null,
        features_en: null,
        name: null,
        name_ar: null,
        name_en: null,
        price: null,
        users: [],
      },
    };
  },

  methods: {
    handleClientsReachEnd() {
      this.getAllUsers();
    },
    handleClientsSearch(searchQuery) {
      if (this.clientsSearchTimeout) {
        clearTimeout(this.clientsSearchTimeout);
      }

      this.clientsSearchTimeout = setTimeout(() => {
        const normalizedSearch = (searchQuery || "").trim();
        if (normalizedSearch === this.clientsPagination.search) {
          return;
        }

        this.clientsPagination.search = normalizedSearch;
        this.allUsers = [];
        this.resetClientsPagination();
        this.clientsPagination.search = normalizedSearch;
        this.getAllUsers();
      }, 400);
    },
    resetClientsPagination() {
      this.clientsPagination.page = 0;
      this.clientsPagination.hasMore = true;
      this.clientsPagination.isLoading = false;
    },
    async getAllUsers() {
      if (this.clientsPagination.isLoading || !this.clientsPagination.hasMore) {
        return;
      }

      this.clientsPagination.isLoading = true;

      try {
        const searchQuery = this.clientsPagination.search
          ? `&name=${encodeURIComponent(this.clientsPagination.search)}`
          : "";

        let res = await this.$axios({
          method: "GET",
          url: `clients?page=${this.clientsPagination.page}&limit=${this.clientsPagination.limit}&is_active=1${searchQuery}`,
        });

        const clientsList = res?.data?.data?.data || [];
        if (clientsList.length === 0) {
          this.clientsPagination.hasMore = false;
          return;
        }

        const existingIds = new Set(this.allUsers.map((item) => item.id));

        clientsList.forEach((user) => {
          const clientItem = {
            id: user.user?.id,
            name: `${user.user?.name} (${user.user?.mobile})`,
          };

          if (clientItem.id && !existingIds.has(clientItem.id)) {
            this.allUsers.push(clientItem);
            existingIds.add(clientItem.id);
          }
        });

        this.clientsPagination.page += 1;
        if (clientsList.length < this.clientsPagination.limit) {
          this.clientsPagination.hasMore = false;
        }
      } catch (error) {
        console.log(error.response.data.message);
      } finally {
        this.clientsPagination.isLoading = false;
      }
    },
    async getDataToEdit() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `package`,
        });

        this.data.duration = res.data.data.Package.duration;
        this.data.features = res.data.data.Package.features;
        this.data.features_ar = res.data.data.Package.features_ar;
        this.data.features_en = res.data.data.Package.features_en;
        this.data.id = res.data.data.Package.id;
        this.data.name = res.data.data.Package.name;
        this.data.name_ar = res.data.data.Package.name_ar;
        this.data.name_en = res.data.data.Package.name_en;
        this.data.price = res.data.data.Package.price;

        // Load selected users if they exist
        if (res.data.data.Package.users) {
          this.data.users = res.data.data.Package.users.map((user) => {
            return {
              ...user,
              name: `${user.name} (${user.mobile})`,
            };
          });

          const existingIds = new Set(this.allUsers.map((item) => item.id));
          this.data.users.forEach((user) => {
            if (user.id && !existingIds.has(user.id)) {
              this.allUsers.push({
                id: user.id,
                name: user.name,
              });
            }
          });
        }

        // End:: Set Data
      } catch (error) {
        console.log(error.response.data.message);
      }
    },
    // End:: Get Data To Edit

    // Start:: Submit Form
    async submitForm() {
      this.isWaitingRequest = !this.isWaitingRequest;

      const REQUEST_DATA = new FormData();
      REQUEST_DATA.append("_method", "PUT");
      REQUEST_DATA.append("name[ar]", this.data.name_ar);
      REQUEST_DATA.append("name[en]", this.data.name_en);
      REQUEST_DATA.append("features[ar]", this.data.features_ar);
      REQUEST_DATA.append("features[en]", this.data.features_en);
      REQUEST_DATA.append("price", this.data.price);
      REQUEST_DATA.append("duration", this.data.duration);

      if (this.data.users && this.data.users.length > 0) {
        console.log(this.data.users);
        this.data.users.forEach((user) => {
          REQUEST_DATA.append("users[]", user.id);
        });
      }

      try {
        await this.$axios({
          method: "POST",
          url: `package`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.savedSuccessfully"));
        this.getDataToEdit();
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(error.response.data.message);
      }
    },
    // End:: Submit Form

    // Start:: validate Form Inputs
    validateFormInputs() {
      this.submitForm();
    },
    // End:: validate Form Inputs
  },

  created() {
    // Start:: Fire Methods
    this.getAllUsers();
    this.getDataToEdit();
    // End:: Fire Methods
  },
  beforeDestroy() {
    if (this.clientsSearchTimeout) {
      clearTimeout(this.clientsSearchTimeout);
    }
  },
};
</script>

<style lang="scss" scoped>
.item {
  gap: 10px;
}

.all_action {
  display: flex;
  gap: 15px;
}

.add_another {
  border: none;
  padding: 8px;
  width: 40px;
  height: 40px;
  border: 1px solid var(--light_gray_clr);
  border-radius: 50%;
  font-size: 18px;
  color: var(--light_gray_clr);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin: auto;

  .fa-trash {
    color: #ff2159;
    cursor: pointer;
  }
}
</style>
